import type {
  LoginDto,
  RegisterDto,
  AuthResponse,
  RefreshTokenResponse,
  RefreshTokenDto,
  User,
  UpdateProfileDto,
  UploadAvatarResponse,
} from '@/types/auth'
import type { ApiError } from '@/types/api'
import type {
  UnsubscribeQuery,
  UnsubscribePreferencesResponse,
  UnsubscribeRequest,
  UnsubscribeSuccessResponse,
  TokenValidationResponse,
} from '@/types/unsubscribe'
import type {
  Workspace,
  WorkspacesResponse,
  CreateWorkspaceDto,
  UpdateWorkspaceDto,
  Document,
  DocumentsResponse,
  CreateDocumentDto,
  UpdateDocumentDto,
  DocumentQuery
} from '@/types/workspace'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''
if (!API_BASE_URL) {
  console.error(
    'VITE_API_URL environment variable is missing.',
    'Current env:',
    import.meta.env,
    'Expected: VITE_API_URL should be set during build time.'
  )
  throw new Error(
    'VITE_API_URL environment variable is required. Please ensure it is set in your build environment.'
  )
}

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public errors?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiClientError'
  }
}

class ApiClient {
  private baseURL: string
  private refreshTokenValue: string | null = null

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL
    this.loadTokensFromStorage()
  }

  private loadTokensFromStorage(): void {
    this.refreshTokenValue = localStorage.getItem('refresh_token')
  }

  private getCSRFToken(): string {
    // CSRF token must be provided by the server in the meta tag
    // Server should set this when serving the HTML page
    const token = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content')
    return token || ''
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {} // eslint-disable-line no-undef
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`

    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    }

    // Only set Content-Type for requests with body
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json'
    }

    // Only add CSRF token for state-changing requests and if token exists
    const csrfToken = this.getCSRFToken()
    if (
      csrfToken &&
      ['POST', 'PUT', 'PATCH', 'DELETE'].includes(
        options.method?.toUpperCase() || 'GET'
      )
    ) {
      headers['X-CSRF-Token'] = csrfToken
    }

    // eslint-disable-next-line no-undef
    const config: RequestInit = {
      headers,
      credentials: 'include',
      ...options,
    }

    // Add auth token if available
    const token = this.getAccessToken()
    if (token) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      }
    }

    try {
      const response = await fetch(url, config)

      // Handle empty responses for logout, etc.
      if (
        response.status === 204 ||
        response.headers.get('content-length') === '0'
      ) {
        return {} as T
      }

      const data = await response.json()

      if (!response.ok) {
        const error = data as ApiError
        throw new ApiClientError(
          error.statusCode || response.status,
          error.code || 'UNKNOWN_ERROR',
          error.message || `HTTP error! status: ${response.status}`,
          error.errors
        )
      }

      return data
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error
      }

      // Log detailed error information for debugging (remove in production)
      if (import.meta.env.DEV) {
        console.error('API request failed:', {
          endpoint,
          method: config.method,
          error: error instanceof Error ? error.message : 'Unknown error',
          timestamp: new Date().toISOString(),
        })
      }

      // Create more specific error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new ApiClientError(
          0,
          'NETWORK_ERROR',
          'Network connection failed'
        )
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiClientError(0, 'REQUEST_TIMEOUT', 'Request timed out')
      }

      throw new ApiClientError(
        0,
        'UNKNOWN_ERROR',
        'An unexpected error occurred. Please try again.'
      )
    }
  }

  // SECURITY WARNING: Token storage in localStorage is vulnerable to XSS attacks
  // TODO: Migrate to httpOnly cookies for production deployment
  private getAccessToken(): string | null {
    return localStorage.getItem('access_token')
  }

  private setTokens(accessToken: string, refreshToken: string): void {
    // SECURITY WARNING: This should use httpOnly cookies in production
    localStorage.setItem('access_token', accessToken)
    localStorage.setItem('refresh_token', refreshToken)
    this.refreshTokenValue = refreshToken
  }

  private removeTokens(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    this.refreshTokenValue = null
  }

  async login(credentials: LoginDto): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })

    this.setTokens(response.accessToken, response.refreshToken)
    return response
  }

  async register(userData: RegisterDto): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    })

    this.setTokens(response.accessToken, response.refreshToken)
    return response
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    if (!this.refreshTokenValue) {
      throw new ApiClientError(
        401,
        'NO_REFRESH_TOKEN',
        'No refresh token available'
      )
    }

    const payload: RefreshTokenDto = {
      refreshToken: this.refreshTokenValue,
    }

    const response = await this.request<RefreshTokenResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify(payload),
    })

    this.setTokens(response.accessToken, response.refreshToken)
    return response
  }

  async logout(): Promise<void> {
    try {
      await this.request<{ success: boolean; message: string }>(
        '/auth/logout',
        {
          method: 'POST',
        }
      )
    } finally {
      this.removeTokens()
    }
  }

  async getCurrentUser(): Promise<User> {
    return await this.request<User>('/auth/me')
  }

  async verifySession(): Promise<boolean> {
    try {
      await this.getCurrentUser()
      return true
    } catch (error) {
      if (error instanceof ApiClientError && error.statusCode === 401) {
        // Try to refresh the token
        try {
          await this.refreshToken()
          await this.getCurrentUser()
          return true
        } catch {
          this.removeTokens()
          return false
        }
      }
      return false
    }
  }

  async verifyEmail(
    token: string
  ): Promise<{ success: boolean; message: string }> {
    return await this.request<{ success: boolean; message: string }>(
      `/auth/verify-email?token=${encodeURIComponent(token)}`
    )
  }

  async resendVerificationEmail(): Promise<{
    success: boolean
    message: string
  }> {
    return await this.request<{ success: boolean; message: string }>(
      '/auth/resend-verification',
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    )
  }

  async forgotPassword(
    email: string
  ): Promise<{ success: boolean; message: string }> {
    return await this.request<{ success: boolean; message: string }>(
      '/auth/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      }
    )
  }

  async resetPassword(
    token: string,
    password: string,
    passwordConfirm: string
  ): Promise<{ success: boolean; message: string }> {
    return await this.request<{ success: boolean; message: string }>(
      '/auth/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ token, password, passwordConfirm }),
      }
    )
  }

  // Google OAuth methods
  async getGoogleAuthUrl(): Promise<{ url: string; state?: string }> {
    console.log('Making request to /auth/google...')
    try {
      const response = await this.request<{ authUrl: string; state: string }>(
        '/auth/google'
      )
      console.log('Response from /auth/google:', response)
      return { url: response.authUrl, state: response.state }
    } catch (error) {
      console.error('Error in getGoogleAuthUrl:', error)
      throw error
    }
  }

  async handleGoogleCallback(
    code: string,
    state?: string
  ): Promise<{ success: boolean; redirectUrl?: string }> {
    const params = new URLSearchParams({ code })
    if (state) params.append('state', state)

    return await this.request<{ success: boolean; redirectUrl?: string }>(
      `/auth/google/callback?${params.toString()}`
    )
  }

  async exchangeGoogleToken(
    code: string,
    state?: string
  ): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/google/token', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    })

    this.setTokens(response.accessToken, response.refreshToken)
    return response
  }

  // Health check endpoints
  async getHealth(): Promise<{ status: string; timestamp: string }> {
    return await this.request<{ status: string; timestamp: string }>('/health')
  }

  async getStatus(): Promise<{
    service: string
    status: string
    timestamp: string
    version: string
    uptime: number
    environment: string
    services: Record<string, string>
  }> {
    return await this.request<{
      service: string
      status: string
      timestamp: string
      version: string
      uptime: number
      environment: string
      services: Record<string, string>
    }>('/')
  }

  async submitFeedback(data: { message: string; category: string }): Promise<{
    success: boolean
    feedback: {
      id: string
      userId: string
      message: string
      rating: number | null
      category: string | null
      status: string
      adminResponse: string | null
      respondedAt: string | null
      createdAt: string
      updatedAt: string
    }
  }> {
    return await this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    })
  }

  // Profile management methods
  async uploadAvatar(file: File): Promise<UploadAvatarResponse> {
    const formData = new FormData()
    formData.append('avatar', file)

    const url = `${this.baseURL}/users/profile/avatar`

    const headers: Record<string, string> = {}

    // Add CSRF token for POST request
    const csrfToken = this.getCSRFToken()
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
    }

    // Add auth token if available
    const token = this.getAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const error = await response.json()
          errorMessage = error.message || errorMessage
          throw new ApiClientError(
            error.statusCode || response.status,
            error.code || 'UPLOAD_ERROR',
            errorMessage
          )
        } catch {
          // If JSON parsing fails, throw with status info
          throw new ApiClientError(
            response.status,
            'UPLOAD_ERROR',
            errorMessage
          )
        }
      }

      return await response.json()
    } catch (error) {
      console.error('Avatar upload failed:', error)

      if (error instanceof ApiClientError) {
        throw error
      }

      // Handle network errors and ORB errors
      if (error instanceof TypeError) {
        throw new ApiClientError(
          0,
          'NETWORK_ERROR',
          'Network error during upload. Please check if the backend is running and accessible.'
        )
      }

      throw new ApiClientError(
        0,
        'UNKNOWN_ERROR',
        'An unexpected error occurred during upload.'
      )
    }
  }

  async updateProfile(data: UpdateProfileDto): Promise<{ user: User }> {
    return await this.request<{ user: User }>('/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  // Unsubscribe methods
  async getUnsubscribePreferences(
    query: UnsubscribeQuery
  ): Promise<UnsubscribePreferencesResponse> {
    const params = new URLSearchParams({
      token: query.token,
      email: query.email,
    })

    return await this.request<UnsubscribePreferencesResponse>(
      `/unsubscribe?${params.toString()}`
    )
  }

  async updateUnsubscribePreferences(
    data: UnsubscribeRequest
  ): Promise<UnsubscribeSuccessResponse> {
    return await this.request<UnsubscribeSuccessResponse>('/unsubscribe', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async validateUnsubscribeToken(
    query: UnsubscribeQuery
  ): Promise<TokenValidationResponse> {
    const params = new URLSearchParams({
      token: query.token,
      email: query.email,
    })

    return await this.request<TokenValidationResponse>(
      `/unsubscribe/validate?${params.toString()}`
    )
  }

  // Workspace management methods
  async getWorkspaces(): Promise<WorkspacesResponse> {
    return await this.request<WorkspacesResponse>('/workspaces')
  }

  async getWorkspace(workspaceId: string): Promise<Workspace> {
    return await this.request<Workspace>(`/workspaces/${workspaceId}`)
  }

  async createWorkspace(data: CreateWorkspaceDto): Promise<Workspace> {
    return await this.request<Workspace>('/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateWorkspace(workspaceId: string, data: UpdateWorkspaceDto): Promise<Workspace> {
    return await this.request<Workspace>(`/workspaces/${workspaceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteWorkspace(workspaceId: string): Promise<void> {
    return await this.request<void>(`/workspaces/${workspaceId}`, {
      method: 'DELETE',
    })
  }

  // Document management methods
  async getDocuments(query: DocumentQuery): Promise<DocumentsResponse> {
    const params = new URLSearchParams()
    params.append('workspaceId', query.workspaceId)
    if (query.limit) params.append('limit', query.limit.toString())
    if (query.offset) params.append('offset', query.offset.toString())
    if (query.archived !== undefined) params.append('archived', query.archived.toString())
    if (query.search) params.append('search', query.search)

    return await this.request<DocumentsResponse>(`/documents?${params.toString()}`)
  }

  async getDocument(documentId: string): Promise<Document> {
    return await this.request<Document>(`/documents/${documentId}`)
  }

  async createDocument(data: CreateDocumentDto): Promise<Document> {
    return await this.request<Document>('/documents', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateDocument(documentId: string, data: UpdateDocumentDto): Promise<Document> {
    return await this.request<Document>(`/documents/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async deleteDocument(documentId: string): Promise<void> {
    return await this.request<void>(`/documents/${documentId}`, {
      method: 'DELETE',
    })
  }
}

export const apiClient = new ApiClient()
